import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { CHUNK_LOAD_ERROR_MESSAGES } from 'platform/utilities/lazy-load-with-retry';

import { isBot, canInitDatadog, dataDogLogger } from './utilities';

/**
 * @typedef {Object} CustomRumSettings https://docs.datadoghq.com/real_user_monitoring/browser/setup/client/?tab=rum#initialization-parameters
 * @property {string} applicationId - Datadog application ID
 * @property {string} clientToken - Datadog client token
 * @property {string} defaultPrivacyLevel - Default privacy level
 * @property {string} env - Environment name (default: 'production')
 * @property {string} service - Datadog service name
 * @property {number} sessionSampleRate - Sample rate for sessions
 * @property {number} sessionReplaySampleRate - Sample rate for session replay
 * @property {string} site - Datadog site (default: 'ddog-gov.com')
 * @property {boolean} trackInteractions - Whether to track user interactions
 * @property {boolean} trackUserInteractions - Whether to track user interactions
 * @property {boolean} trackFrustrations - Whether to track user frustrations
 * @property {boolean} trackResources - Whether to track resources
 * @property {boolean} trackLongTasks - Whether to track long tasks
 * @property {string} version - Datadog version
 */
/**
 * Compose multiple Datadog `beforeSend` hooks into a single hook.
 *
 * Each hook runs in order and may mutate the event (e.g. to scrub PII from an
 * action name). The composed hook discards the event (returns `false`) if ANY
 * hook returns `false`, matching Datadog's contract; otherwise it returns
 * `true`. This lets a platform-level privacy baseline and an app-specific hook
 * both run instead of the app's hook silently replacing the platform one via
 * object spread.
 *
 * @param {...(Function|undefined)} hooks - beforeSend hooks (falsy entries are ignored)
 * @returns {Function|undefined} composed hook, or undefined when no hooks are provided
 */
const composeBeforeSend = (...hooks) => {
  const active = hooks.filter(hook => typeof hook === 'function');
  if (active.length === 0) {
    return undefined;
  }
  if (active.length === 1) {
    return active[0];
  }
  return (event, context) => {
    // Run every hook so each can mutate the event; drop the event only if one
    // explicitly returns false.
    let keep = true;
    active.forEach(hook => {
      if (hook(event, context) === false) {
        keep = false;
      }
    });
    return keep;
  };
};

/**
 * True when a message string matches one of the known chunk / dynamic-import
 * load-failure substrings. Message-only so it can back both the RUM
 * (`event.error.message`) and Logs (`log.message`) baselines.
 *
 * @param {string} message
 * @returns {boolean}
 */
const isChunkLoadConsoleMessage = message => {
  if (typeof message !== 'string') {
    return false;
  }
  const lowered = message.toLowerCase();
  return CHUNK_LOAD_ERROR_MESSAGES.some(phrase => lowered.includes(phrase));
};

/**
 * Platform baseline `beforeSend` for RUM. Drops the transient chunk /
 * dynamic-import failures that RUM auto-collects from `console.error`.
 *
 * Our retry logic (platform `lazyWithRetry` + component-library
 * `retryDynamicImport`) recovers the large majority of these, but RUM records a
 * `console`-source error for each failed attempt BEFORE the retry (or a page
 * reload) succeeds, independently of the JS retry logic. Datadog RUM
 * measurement (Aug 2026) found these console events over-report ~14x vs. the
 * authoritative terminal failures, and ~93% of them were for loads that
 * ultimately recovered.
 *
 * The message list is imported from `lazy-load-with-retry` (the same substrings
 * the retry logic matches on) so the two never drift. It deliberately excludes
 * the bare "Load failed" wording (Safari's generic fetch-failure message):
 * that is not specific to chunk loading, so dropping it would also suppress
 * legitimate API-failure console errors.
 *
 * The authoritative "we gave up after all retries" signal is reported
 * separately via `datadogRum.addError(err, { source: 'wc-loader' })`, which
 * arrives as an `error.source === 'custom'` event and is therefore never
 * dropped here. Only `console`-source chunk/module-import errors are removed;
 * `network`, `source`, and `custom` errors are always kept.
 *
 * @param {Object} event - RUM event passed to `beforeSend`
 * @returns {boolean} `false` to drop the event, `true` to keep it
 */
const dropRecoveredChunkLoadErrors = event => {
  if (event?.type !== 'error') {
    return true;
  }
  const { source, message } = event.error || {};
  if (source !== 'console') {
    return true;
  }
  return !isChunkLoadConsoleMessage(message);
};

/**
 * Platform baseline `beforeSend` for Datadog Logs. Mirrors
 * `dropRecoveredChunkLoadErrors`, but for the Logs event shape: the
 * discriminator is `log.origin` (not `event.error.source`) and the message text
 * lives in `log.message`.
 *
 * `forwardConsoleLogs: ['error']` forwards every `console.error` to Logs, so the
 * same recovered chunk/dynamic-import failures RUM over-reports also land here
 * (~64k/week, all `@origin:console`). Only `error`-status, `console`-origin
 * chunk/module-import messages are dropped; everything else — including
 * `source`-origin runtime errors and network errors — is kept. The
 * authoritative terminal signal never reaches Logs (wc-loader reports via
 * `DD_RUM.addError`), so nothing authoritative is at risk here.
 *
 * @param {Object} log - Logs event passed to `beforeSend`
 * @returns {boolean} `false` to drop the log, `true` to keep it
 */
const dropRecoveredChunkLoadLogs = log => {
  if (log?.status !== 'error' || log?.origin !== 'console') {
    return true;
  }
  return !isChunkLoadConsoleMessage(log.message);
};

/**
 * Initialize Datadog RUM directly, if not using a feature flag
 * @param {CustomRumSettings} customRumSettings
 */
const initializeRealUserMonitoring = (
  customRumSettings,
  checkInit = canInitDatadog,
) => {
  // Prevent RUM from re-initializing the SDK OR running on local/CI
  // environments.
  if (checkInit()) {
    const defaults = {
      // Required settings
      // applicationId: '{UUID}',
      // clientToken: 'pubb{ID}',
      // service: 'benefits-{app name}',

      // Default settings
      version: process.env.APP_VERSION || '1.0.0',
      site: 'ddog-gov.com',
      // see src/site/constants/vsp-environments.js for defaults
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: true,
      env: environment.vspEnvironment(), // 'production'
      sessionReplaySampleRate: 100,
      silentMultipleInit: true, // silently ignore multiple inits
      trackFrustrations: true,
      trackInteractions: true,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      trackViewsManually: false,
      // Platform-wide beforeSend baseline: drop the transient chunk /
      // dynamic-import console errors that our retry logic recovers from. This
      // composes with (is not overridden by) an app-provided beforeSend below.
      beforeSend: dropRecoveredChunkLoadErrors,
    };
    const custom = customRumSettings || {};
    const config = { ...defaults, ...custom };
    const beforeSend = composeBeforeSend(
      defaults.beforeSend,
      custom.beforeSend,
    );
    if (beforeSend) {
      config.beforeSend = beforeSend;
    } else {
      delete config.beforeSend;
    }

    datadogRum.init(config);

    // If sessionReplaySampleRate > 0, we need to manually start the recording
    datadogRum.startSessionReplayRecording();
  }
};

/**
 * @typedef {Object} CustomLogSettings https://docs.datadoghq.com/logs/log_collection/javascript/?tab=us#configuration
 * @property {string} applicationId - Datadog application ID
 * @property {string} clientToken - Datadog client token
 * @property {string} env - Environment name
 * @property {boolean} forwardErrorsToLogs - Whether to forward errors to logs
 * @property {Array<string>} forwardConsoleLogs - Console log levels to forward
 * @property {Array<string>} forwardReports - Reports to forward
 * @property {number} sessionSampleRate - Sample rate for sessions
 * @property {string} service - Datadog service name
 * @property {string} site - Datadog site (default: 'ddog-gov.com')
 * @property {number} telemetrySampleRate - Sample rate for telemetry
 * @property {string} version - Datadog version
 */
/**
 * Initialize Datadog RUM directly, if not using a feature flag
 * @param {CustomLogSettings} customLogSettings
 */
const initializeBrowserLogging = (
  customLogSettings,
  checkInit = canInitDatadog,
) => {
  // Prevent LOGS from re-initializing the SDK OR running on local/CI
  // environments.
  if (checkInit()) {
    const defaults = {
      // Required settings
      // applicationId: '{UUID}',
      // clientToken: 'pubb{ID}',
      // service: 'benefits-{app name}',

      // default settings
      version: process.env.APP_VERSION || '1.0.0',
      site: 'ddog-gov.com',
      // see src/site/constants/vsp-environments.js for defaults
      env: environment.vspEnvironment(), // 'production'
      forwardConsoleLogs: ['error'],
      forwardErrorsToLogs: true,
      forwardReports: [],
      silentMultipleInit: true, // silently ignore multiple inits
      telemetrySampleRate: 100,
      // Platform-wide beforeSend baseline: drop the transient chunk /
      // dynamic-import console errors that our retry logic recovers from
      // (forwarded here via forwardConsoleLogs). Composes with (is not
      // overridden by) an app-provided beforeSend below.
      beforeSend: dropRecoveredChunkLoadLogs,
    };
    const custom = customLogSettings || {};
    const config = { ...defaults, ...custom };
    const beforeSend = composeBeforeSend(
      defaults.beforeSend,
      custom.beforeSend,
    );
    if (beforeSend) {
      config.beforeSend = beforeSend;
    } else {
      delete config.beforeSend;
    }

    datadogLogs.init(config);
  }
};

/**
 * Look up Datadog config from the build-time injected manifest configs.
 * Returns the config for the given service name, or an empty object.
 * @param {string} service - Datadog service name (matches manifest.json datadog.service or entryName)
 * @returns {Object} Config from manifest.json datadog block, or {}
 */
const getManifestConfig = service => {
  try {
    let configs = process.env.DATADOG_CONFIGS || {};
    // esbuild injects this as an object literal at build time; tolerate a JSON
    // string too so the lookup is exercisable outside the bundler (e.g. tests).
    if (typeof configs === 'string') {
      configs = JSON.parse(configs);
    }
    return configs[service] || {};
  } catch {
    return {};
  }
};

// Initialize Datadog RUM behind feature flag
const useBrowserMonitoring = ({
  toggleName,
  loggedIn,
  checkInit = canInitDatadog,
  ...settings
} = {}) => {
  // Look up manifest config by service name if available
  const manifestConfig = useMemo(
    () => getManifestConfig(settings.service),
    [settings.service],
  );
  const mergedToggleName = toggleName || manifestConfig.toggleName || '';
  // `settings` is a fresh object every render (rest spread), so serialize it to
  // a stable key and memoize `merged` to keep the effect dependency referentially
  // stable across renders.
  const settingsKey = JSON.stringify(settings);
  const merged = useMemo(
    () => ({ ...manifestConfig, ...settings }),
    [manifestConfig, settingsKey],
  );

  const { TOGGLE_NAMES, useToggleLoadingValue } = useFeatureToggle();

  const toggleKey = mergedToggleName
    ? TOGGLE_NAMES[mergedToggleName]
    : undefined;

  const isLoadingFeatureFlags = useToggleLoadingValue();
  const rawToggleValue = useSelector(state =>
    toggleKey ? state?.featureToggles?.[toggleKey] : undefined,
  );
  const isBrowserMonitoringEnabled = toggleKey ? rawToggleValue : true;

  useEffect(
    () => {
      // Allow RUM for unauthenticated forms; pass in undefined loggedIn
      if (isLoadingFeatureFlags || loggedIn === false) {
        return;
      }
      if (!merged.applicationId || !merged.clientToken || !merged.service) {
        // eslint-disable-next-line no-console
        console.error(
          'Datadog RUM & monitoring initialization requires applicationId, clientToken, and service. ' +
            'Add a datadog block to your manifest.json or pass them directly.',
        );
        return;
      }

      // Allows initialization with no feature flag, or when toggle is enabled
      if (!mergedToggleName || isBrowserMonitoringEnabled) {
        initializeRealUserMonitoring(merged, checkInit);
        initializeBrowserLogging(merged, checkInit);
      } else {
        delete window.DD_RUM;
        delete window.DD_LOGS;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [merged, isBrowserMonitoringEnabled, isLoadingFeatureFlags],
  );

  return null;
};

export {
  canInitDatadog,
  composeBeforeSend,
  dropRecoveredChunkLoadErrors,
  dropRecoveredChunkLoadLogs,
  getManifestConfig,
  initializeBrowserLogging,
  initializeRealUserMonitoring,
  isBot,
  useBrowserMonitoring,
  dataDogLogger,
};
